import { faPaperclip as AttachmentIcon } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert, Box, FormControlLabel, FormGroup } from '@mui/material';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import {
  type ChangeEvent,
  forwardRef,
  useCallback,
  useMemo,
  useState
} from 'react';
import {
  Controller,
  type FieldError,
  type FieldErrorsImpl,
  type Merge,
  useFieldArray,
  useFormContext
} from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';

import { getFileExtension } from '@/utils/Common.utils';

import useStyles from './FileUploader.styles';
import type {
  CustomFile,
  FileDetails,
  FileUploaderProps
} from './FileUploader.types';

const FileUploader = forwardRef<HTMLDivElement, FileUploaderProps>(
  (props, ref) => {
    const {
      acceptedTypes,
      acceptedTypesButtonLabel,
      errorMessage,
      isRequired = false,
      selectedFileListPlacement = 'right',
      singleFile,
      skipCheckFiles = false,
      titleButton = 'button',
      titleButtonTooltip,
      titleButtonVariant,

      titleText
    } = props;
    const { control, getValues } = useFormContext();
    const { replace } = useFieldArray({
      name: 'attachments',
      control
    });
    const [areFilesChecked, setAreFilesChecked] = useState(false);
    const [open, setOpen] = useState(false);
    const [rejectedFiles, setRejectedFiles] = useState<File[]>([]);
    const [selectedFiles, setSelectedFiles] = useState<FileDetails[]>([]);
    const [warning, setWarning] = useState(false);
    const [fileTypeError, setFileTypeError] = useState(false);
    const { classes } = useStyles({ selectedFileListPlacement });

    const uploadFiles = getValues('attachments');

    const removeFile = (file: FileDetails) => {
      const newFileArray = uploadFiles.filter(
        (f: FileDetails) =>
          f.name !== file.name || f.file_name !== file.file_name
      );
      setAreFilesChecked(false);
      replace(newFileArray);
    };

    const addFile = (event: ChangeEvent<HTMLInputElement>) => {
      setAreFilesChecked(false);
      const { files } = event.target;

      const newFiles = [...(files || [])].filter(
        (file) => !uploadFiles.map((f: File) => f.name).includes(file.name)
      );

      for (let i = 0; i < newFiles.length; i += 1) {
        if (acceptedTypes) {
          const fileType = newFiles[i].type;
          const fileExtension = getFileExtension(newFiles[i].name);

          // If file has a default MIME type, check it
          // If file type is empty/null, check the file extension
          if (
            (fileType && !acceptedTypes.includes(fileType)) ||
            (!fileType && !acceptedTypes.includes(fileExtension))
          ) {
            setFileTypeError(true);
            return;
          }
        }
        setFileTypeError(false);

        if (
          skipCheckFiles === false &&
          (newFiles[i] as CustomFile).safe === undefined
        ) {
          (newFiles[i] as CustomFile).safe = false;
        }
      }

      if (skipCheckFiles === true) {
        if (singleFile && newFiles.length > 0) {
          replace([...newFiles.slice(0, 1)]);
        } else {
          replace([...uploadFiles, ...newFiles]);
        }
        return;
      }

      // no new files, don't update
      if (newFiles.length !== 0) {
        setSelectedFiles([...newFiles]);
        setOpen(true);
      }
    };

    const checkFiles = () => {
      let shouldWarn = false;
      for (let i = 0; i < selectedFiles.length; i += 1) {
        if (selectedFiles[i].safe === false) {
          // check for zip
          if (
            selectedFiles[i].name.split('.').pop() === 'zip' ||
            selectedFiles[i].file_name?.split('.').pop() === 'zip'
          ) {
            // TODO: enable this code block - currently, the api cannot handle the renaming of files prior to save (adding "malicious" to zip files)
            // const name = selectedFiles[i].name || selectedFiles[i].file_name;
            // if (name !== undefined) {
            //   const dotIndex = name.lastIndexOf('.');
            //   if (dotIndex === -1) {
            //     selectedFiles[i].updatedName = `${name}(malicious)`;
            //   } else {
            //     selectedFiles[i].updatedName = `${name.substring(
            //       0,
            //       dotIndex
            //     )}(malicious)${name.substring(dotIndex)}`;
            //   }
            // }
          } else {
            shouldWarn = true;
            setRejectedFiles((files) => {
              files.push(selectedFiles[i]);
              return files;
            });
          }
        }
      }

      setAreFilesChecked(true);
      if (shouldWarn) {
        setWarning(true);
      } else {
        setOpen(false);
      }
      const selectedFilesCopy = [...selectedFiles];
      for (let i = 0; i < selectedFilesCopy.length; i += 1) {
        if (selectedFilesCopy[i].updatedName) {
          selectedFilesCopy[i].safe = true;
        }
      }
      const filteredFiles = selectedFiles.filter(
        (file) =>
          file.safe === true ||
          (file.safe === false &&
            (file.name.split('.').pop() === 'zip' ||
              file.file_name?.split('.').pop() === 'zip'))
      );

      if (singleFile && filteredFiles.length > 0) {
        replace([...filteredFiles]);
      } else {
        replace([...uploadFiles, ...filteredFiles]);
      }
    };

    const updateSafe = (checked: boolean, file: FileDetails) => {
      const foundFile = selectedFiles.find((f: File) => f.name === file.name);
      if (foundFile) {
        foundFile.safe = checked;
      }
    };

    const handleClick = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
      const target = e.target as HTMLInputElement;
      target.value = '';
      return null;
    };

    const handleClose = () => {
      setAreFilesChecked(false);
      setOpen(false);
      setRejectedFiles([]);
      setWarning(false);
    };

    const renderFileUploads =
      uploadFiles.length > 0 &&
      uploadFiles.map((file: FileDetails) => (
        <div key={uuidv4()}>
          <Chip
            label={
              file.updatedName || file.name || file.filename || file.file_name
            }
            onDelete={() => removeFile(file)}
            size="small"
            className={classes.fileUploaderChip}
            clickable={false}
            sx={{ mt: 0.6 }}
          />
        </div>
      ));

    // TODO: fix any
    const errorAlerts = useCallback(
      (
        attachmentError:
          | FieldError
          | Merge<FieldError, FieldErrorsImpl<any>>
          | undefined
      ) => {
        if (!errorMessage) return null;
        if (fileTypeError || attachmentError) {
          return (
            <Alert sx={{ mb: 1 }} severity="error">
              {errorMessage}
            </Alert>
          );
        }
        return null;
      },
      [fileTypeError]
    );

    const renderChips = useMemo(
      () =>
        uploadFiles.length > 0 && (
          <Stack
            direction="column"
            sx={{ flexWrap: 'wrap' }}
            spacing={1}
            useFlexGap
          >
            {renderFileUploads}
          </Stack>
        ),
      [uploadFiles.length, renderFileUploads]
    );

    const uploaderTitle = useMemo(() => {
      if (titleText) {
        return titleText;
      }
      return singleFile ? 'Add Attachment' : 'Add Attachments';
    }, [singleFile, titleText]);

    return (
      <Stack ref={ref} className={classes.fileUploader} spacing={1}>
        <Controller
          control={control}
          name="attachments"
          rules={{
            required: isRequired
          }}
          render={({ formState }) => (
            <Stack
              spacing={1}
              sx={{
                width: '100%'
              }}
            >
              {errorMessage &&
                (fileTypeError || formState.errors.attachments) && (
                  <Box className={classes.fileUploaderAlert}>
                    {errorAlerts(formState.errors.attachments)}
                  </Box>
                )}
              <Stack
                direction="row"
                sx={{
                  alignItems: 'flex-start',
                  flexWrap: 'wrap',
                  justifyContent:
                    selectedFileListPlacement === 'left'
                      ? 'flex-end'
                      : 'flex-start',
                  gap: 2
                }}
                spacing={2}
              >
                {selectedFileListPlacement === 'left' && renderChips}
                <Tooltip title={titleButtonTooltip} placement="bottom-start">
                  {titleButton === 'button' ? (
                    <Button
                      component="label"
                      startIcon={
                        <FontAwesomeIcon icon={AttachmentIcon} size="xs" />
                      }
                      onClick={(event) => {
                        handleClick(event);
                      }}
                      style={{
                        whiteSpace: 'nowrap'
                      }}
                      variant={titleButtonVariant}
                    >
                      {uploaderTitle}
                      {acceptedTypesButtonLabel
                        ? ` (${acceptedTypesButtonLabel})`
                        : ''}
                      <InputLabel htmlFor="attachment-input" />
                      <input
                        id="attachment-input"
                        className={classes.fileUploaderFileInput}
                        type="file"
                        onChange={addFile}
                        multiple={!singleFile}
                        {...(acceptedTypes
                          ? { accept: `${acceptedTypes?.join(', ')}` }
                          : null)}
                      />
                    </Button>
                  ) : (
                    <label htmlFor="attachment-input">
                      <IconButton
                        component="span"
                        aria-label="add a file"
                        onClick={(event) => {
                          handleClick(event);
                        }}
                      >
                        <FontAwesomeIcon icon={AttachmentIcon} size="xs" />
                        <input
                          id="attachment-input"
                          className={classes.fileUploaderFileInput}
                          type="file"
                          onChange={addFile}
                          multiple={!singleFile}
                          {...(acceptedTypes
                            ? { accept: `${acceptedTypes?.join(', ')}` }
                            : null)}
                        />
                      </IconButton>
                    </label>
                  )}
                </Tooltip>
                {selectedFileListPlacement === 'right' && renderChips}
              </Stack>
            </Stack>
          )}
        />
        {skipCheckFiles === false && open && (
          <Dialog scroll="paper" open={open} maxWidth="sm" fullWidth>
            {!areFilesChecked && selectedFiles.length > 0 && (
              <>
                <DialogTitle>
                  {selectedFiles.length === 1
                    ? 'Is this file safe?'
                    : 'Are these files safe?'}
                </DialogTitle>
                <DialogContent style={{ paddingTop: 10 }}>
                  Please check the box if this file is safe to run. If it is not
                  safe to run, we ask that you ensure it is in a password
                  protected zip file.
                  <List>
                    {selectedFiles.map((file: FileDetails) => (
                      <FormGroup key={uuidv4()}>
                        {!file.key && (
                          <FormControlLabel
                            label={file.name || file.file_name}
                            control={
                              <Checkbox
                                onChange={(e) =>
                                  updateSafe(e.target.checked, file)
                                }
                                value={file.safe}
                                defaultChecked={file.safe}
                                color="primary"
                              />
                            }
                          />
                        )}
                      </FormGroup>
                    ))}
                  </List>
                </DialogContent>
                <DialogActions>
                  <Button
                    color="primary"
                    onClick={() => {
                      setOpen(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={() => checkFiles()}
                  >
                    Submit
                  </Button>
                </DialogActions>
              </>
            )}
            {warning && areFilesChecked && (
              <>
                <DialogTitle>Try Again</DialogTitle>
                <DialogContent style={{ paddingTop: 10 }}>
                  The following files were rejected:
                  <List>
                    {rejectedFiles.map((file: FileDetails) => (
                      <ListItem key={uuidv4()}>
                        <ListItemText
                          primary={file.file_name || file.name}
                          className={classes.fileUploaderRejected}
                        />
                      </ListItem>
                    ))}
                  </List>
                  Files that are not safe to run should be placed in a password
                  protected zip file. Please ensure this is the case and try
                  again.
                </DialogContent>
                <DialogActions>
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={handleClose}
                  >
                    Close
                  </Button>
                </DialogActions>
              </>
            )}
          </Dialog>
        )}
      </Stack>
    );
  }
);

FileUploader.displayName = 'FileUploader';

export default FileUploader;
